import { useAppDispatch } from "app/store";
import {
  headerSelectedTeamUpdated,
  teamNameUpdated,
} from "features/audits/slice";
import { GaiaConstants } from "utils";
import { Team } from "models/schemas";
import {
  setIsUserCriticalInTeam,
  setLitigationSchemas,
  setNoticeSchemas,
  setAuditSchemas,
  setSelectedTeamMainMetaData,
  setUserTeamCriticalnessMap,
  setCurrentGuestTeam,
} from "features/team-settings/slice";
import { generateAuditDynamicSchemas } from "models/generateAuditSchemas";
import { generateGaiaObjDynamicSchemas } from "models/generateGaiaObjSchemas";

export function useTeamManagement() {
  const dispatch = useAppDispatch();

  const updateSelectedTeam = (selectedTeam: string) => {
    dispatch(teamNameUpdated(selectedTeam));
  };

  // update the header team selection only
  const updateHeaderSelectedTeam = (
    selectedTeam: string,
    selectedTeamDescription: string
  ) => {
    localStorage.setItem(
      GaiaConstants.SELECTED_TEAM_DESCRIPTION,
      selectedTeamDescription
    );
    localStorage.setItem(GaiaConstants.SELECTED_TEAM_NAME, selectedTeam);
    dispatch(headerSelectedTeamUpdated(selectedTeam));
  };

  // update both header team and data team
  const updateHeaderTeamAndDataTeam = (
    selectedTeam: string,
    selectedTeamDescription: string
  ) => {
    updateHeaderSelectedTeam(selectedTeam, selectedTeamDescription);
    updateSelectedTeam(selectedTeam);
  };

  const updateUserCriticalnessInTeam = (
    selectedTeam: string,
    userCriticalnessInTeam: { [key: string]: boolean }
  ) => {
    const isCritical =
      selectedTeam in userCriticalnessInTeam
        ? userCriticalnessInTeam[selectedTeam]
        : false;
    dispatch(setIsUserCriticalInTeam(isCritical));
  };

  const setUserCriticalnessInTeams = (userCriticalnessInTeam: {
    [key: string]: boolean;
  }) => {
    dispatch(setUserTeamCriticalnessMap(userCriticalnessInTeam));
  };

  const updateTeamDataAndSchemas = (
    selectedTeam: string,
    teamSettingMap: Map<string, Team>
  ) => {
    dispatch(
      setSelectedTeamMainMetaData(
        createTeamMetaDataObject(selectedTeam, teamSettingMap)
      )
    );
    const customAttrData =
      teamSettingMap.get(selectedTeam)?.teamCustomAttributesSchema;

    if (customAttrData?.audit) {
      const auditSchemas = generateAuditDynamicSchemas(customAttrData.audit);
      dispatch(setAuditSchemas(auditSchemas));
    }
    if (customAttrData?.notice) {
      const noticeSchemas = generateGaiaObjDynamicSchemas(
        customAttrData.notice
      );
      dispatch(setNoticeSchemas(noticeSchemas));
    }
    if (customAttrData?.litigation) {
      const litigationSchemas = generateGaiaObjDynamicSchemas(
        customAttrData.litigation
      );
      dispatch(setLitigationSchemas(litigationSchemas));
    }
  };

  const createTeamMetaDataObject = (
    selectedTeam: string,
    teamSettingMap: Map<string, Team>
  ): Team => {
    const team = teamSettingMap.get(selectedTeam);
    return team!;
  };

  const setGuestTeamDataAndSchema = (team: Team) => {
    const customAttrData = team.teamCustomAttributesSchema;
    const teamSchemas: any = {
      selectedTeamMainMetaData: team,
    };

    if (customAttrData?.audit) {
      teamSchemas.auditSchemas = generateAuditDynamicSchemas(
        customAttrData.audit
      );
    }
    if (customAttrData?.notice) {
      teamSchemas.noticeSchemas = generateGaiaObjDynamicSchemas(
        customAttrData.notice
      );
    }
    if (customAttrData?.litigation) {
      teamSchemas.litigationSchemas = generateGaiaObjDynamicSchemas(
        customAttrData.litigation
      );
    }

    dispatch(setCurrentGuestTeam(teamSchemas));
  };

  return {
    updateSelectedTeam,
    updateHeaderSelectedTeam,
    updateHeaderTeamAndDataTeam,
    updateUserCriticalnessInTeam,
    updateTeamDataAndSchemas,
    setUserCriticalnessInTeams,
    setGuestTeamDataAndSchema,
    createTeamMetaDataObject,
  };
}
